import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { GetMatchModelExt } from 'core/entities';
import { DateTime } from 'luxon';
import { GameMode, MatchTeam, SetScoreGoalRole } from 'models/enums';
import { CreateMatchModel, GetMatchModel, GetMatchSetModel, MatchPlayerModel, MatchSetModel } from 'models/matches';

export interface IEditMatchModalContext {
    match: GetMatchModelExt;
}

interface INewGoal {
    elapsedSeconds: number;
}

@Component({
    selector: 'app-edit-match-modal',
    imports: [
        FormsModule,
        MatDialogTitle,
        MatDialogContent,
        MatDialogActions,
        MatAccordion,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatExpansionPanelTitle,
        MatFormField,
        MatLabel,
        MatInput,
        MatButton,
        MatIconButton,
        MatIcon,
        MatTooltip,
        MatDivider,
        TranslateModule,
    ],
    templateUrl: './edit-match-modal.component.html',
    styleUrl: './edit-match-modal.component.scss',
})
export class EditMatchModalComponent {
    private readonly dialogRef = inject<MatDialogRef<EditMatchModalComponent, CreateMatchModel>>(MatDialogRef);
    private readonly context = inject<IEditMatchModalContext>(MAT_DIALOG_DATA);

    public match: CreateMatchModel;
    public readonly SetScoreGoalRole = SetScoreGoalRole;
    public readonly GameMode = GameMode;

    public newGoals: INewGoal[] = [];

    constructor() {
        this.match = structuredClone(this.mapMatchToCreateModel(this.context.match));
        this.newGoals = this.match.sets.map(() => ({ elapsedSeconds: 0 }));
    }

    public isOneAgainstOne(): boolean {
        return this.match.settings?.gameMode === GameMode.OneAgainstOne;
    }

    public swapPositions(setIndex: number, team: 'A' | 'B'): void {
        const set = this.match.sets[setIndex];
        const teamModel = team === 'A' ? set.teamA : set.teamB;

        const oldDefenseId = teamModel.defense.id;
        const oldOffenseId = teamModel.offense.id;

        const temp = teamModel.defense;
        teamModel.defense = teamModel.offense;
        teamModel.offense = temp;

        for (const goal of set.goals) {
            if (goal.idPlayer === oldDefenseId) {
                goal.role = SetScoreGoalRole.Offense;
            } else if (goal.idPlayer === oldOffenseId) {
                goal.role = SetScoreGoalRole.Defense;
            }
        }
    }

    public addGoal(setIndex: number, idPlayer: number, role: SetScoreGoalRole): void {
        this.match.sets[setIndex].goals.push({
            idPlayer,
            role,
            elapsedSeconds: this.newGoals[setIndex].elapsedSeconds,
            createdAt: DateTime.now().toISO({ includeOffset: true }),
        });
        this.recalculateScores(setIndex);
    }

    public addSet(): void {
        const lastSet = this.match.sets.at(-1);
        this.match.sets.push({
            teamA: {
                score: 0,
                positionSwitchCount: 0,
                defense: lastSet?.teamA?.defense ?? this.match.teamA[0],
                offense: lastSet?.teamA?.offense ?? this.match.teamA[1] ?? this.match.teamA[0],
            },
            teamB: {
                score: 0,
                positionSwitchCount: 0,
                defense: lastSet?.teamB?.defense ?? this.match.teamB[0],
                offense: lastSet?.teamB?.offense ?? this.match.teamB[1] ?? this.match.teamB[0],
            },
            goals: [],
            createdAt: DateTime.now().toISO({ includeOffset: true }),
            elapsedSeconds: 0,
        });
        this.newGoals.push({ elapsedSeconds: 0 });
    }

    public removeSet(setIndex: number): void {
        this.match.sets.splice(setIndex, 1);
        this.newGoals.splice(setIndex, 1);
    }

    public removeGoal(setIndex: number, goalIndex: number): void {
        this.match.sets[setIndex].goals.splice(goalIndex, 1);
        this.recalculateScores(setIndex);
    }

    public getPlayerName(setIndex: number, idPlayer: number): string {
        const set = this.match.sets[setIndex];
        const players = [set.teamA.defense, set.teamA.offense, set.teamB.defense, set.teamB.offense];
        return players.find((p) => p?.id === idPlayer)?.displayName ?? String(idPlayer);
    }

    public toDatetimeLocal(iso: string): string {
        return iso ? DateTime.fromISO(iso).toFormat("yyyy-MM-dd'T'HH:mm") : '';
    }

    public fromDatetimeLocal(event: Event): string {
        const value = (event.target as HTMLInputElement).value;
        return value ? DateTime.fromISO(value).toISO({ includeOffset: true }) : '';
    }

    public save(): void {
        this.dialogRef.close(this.match);
    }

    public close(): void {
        this.dialogRef.close();
    }

    private recalculateScores(setIndex: number): void {
        const set = this.match.sets[setIndex];
        const teamAIds = new Set([set.teamA.defense?.id, set.teamA.offense?.id].filter((id) => id != null));
        const teamBIds = new Set([set.teamB.defense?.id, set.teamB.offense?.id].filter((id) => id != null));
        set.teamA.score = set.goals.filter((g) => teamAIds.has(g.idPlayer)).length;
        set.teamB.score = set.goals.filter((g) => teamBIds.has(g.idPlayer)).length;
    }

    private mapMatchToCreateModel(match: GetMatchModelExt): CreateMatchModel {
        const source = match as GetMatchModel;
        const firstSet = source.sets.at(0);

        return {
            uniqueId: source.uniqueId,
            idMatchInvitation: source.idMatchInvitation,
            createdAt: source.createdAt,
            startedAt: source.startedAt ?? source.createdAt,
            endedAt: source.endedAt ?? source.startedAt ?? source.createdAt,
            elapsedSeconds: source.elapsedSeconds,
            teamA: this.mapTeamPlayers(source, MatchTeam.A, firstSet?.idPlayerTeamADefense, firstSet?.idPlayerTeamAOffense),
            teamB: this.mapTeamPlayers(source, MatchTeam.B, firstSet?.idPlayerTeamBDefense, firstSet?.idPlayerTeamBOffense),
            sets: source.sets.map((set) => this.mapSet(set, source.players)),
            settings: {
                gameMode: source.settings?.gameMode,
            },
        };
    }

    private mapSet(set: GetMatchSetModel, players: Array<GetMatchModel['players'][number]>): MatchSetModel {
        return {
            teamA: {
                score: set.scoreTeamA,
                positionSwitchCount: 0,
                defense: this.mapPlayerById(players, set.idPlayerTeamADefense),
                offense: this.mapPlayerById(players, set.idPlayerTeamAOffense),
            },
            teamB: {
                score: set.scoreTeamB,
                positionSwitchCount: 0,
                defense: this.mapPlayerById(players, set.idPlayerTeamBDefense),
                offense: this.mapPlayerById(players, set.idPlayerTeamBOffense),
            },
            goals: set.goals.map((goal) => ({
                idPlayer: goal.idPlayer,
                idPlayerDefense: goal.idPlayerDefense,
                createdAt: goal.createdAt,
                role: goal.role,
                elapsedSeconds: goal.elapsedSeconds,
            })),
            createdAt: set.createdAt,
            elapsedSeconds: set.elapsedSeconds,
        };
    }

    private mapTeamPlayers(match: GetMatchModel, team: MatchTeam, defenseId?: number, offenseId?: number): Array<MatchPlayerModel> {
        const teamPlayers = match.players
            .filter((player) => player.team === team)
            .map((player) => ({
                id: player.idPlayer,
                displayName: player.displayName,
            }));

        if (!defenseId || !offenseId) {
            return teamPlayers;
        }

        const defense = this.mapPlayerById(match.players, defenseId);
        const offense = this.mapPlayerById(match.players, offenseId);
        return [defense, offense];
    }

    private mapPlayerById(players: Array<GetMatchModel['players'][number]>, idPlayer: number): MatchPlayerModel {
        const found = players.find((player) => player.idPlayer === idPlayer);
        return {
            id: idPlayer,
            displayName: found?.displayName ?? '',
        };
    }
}
